import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config/supabase";
import {
  getCurrentWeekNumber,
  getRotationStartDate,
  getDayName,
  formatWeekSelection,
} from "../utils/dateCalculations";
import LoadingSpinner from "../components/LoadingSpinner";
// Import rimossi perché non utilizzati nel codice attuale
// import type { Menu, Snack } from "../config/supabase";

interface DaysData {
  [key: number]: {
    meal: string | null;
    snack: string | null;
  };
}

const MenuPage: React.FC = () => {
  const [weekData, setWeekData] = useState<DaysData>({});
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const daysOfWeek = useMemo(() => [1, 2, 3, 4, 5], []); // Lunedì a Venerdì

  const fetchWeekData = useCallback(
    async (weekNumber: number) => {
      try {
        setLoading(true);

        // Fetch tutti i pasti della settimana specificata
        const { data: menuData, error: menuError } = await supabase
          .from("menus")
          .select("day_of_week, meal")
          .eq("week_number", weekNumber);

        // Fetch tutte le merende
        const { data: snackData, error: snackError } = await supabase
          .from("snacks")
          .select("day_of_week, snack");

        const weekDataTemp: DaysData = {};

        // Inizializza tutti i giorni
        daysOfWeek.forEach((day) => {
          weekDataTemp[day] = { meal: null, snack: null };
        });

        // Popola i dati del menù
        if (!menuError && menuData) {
          menuData.forEach((menu: any) => {
            if (weekDataTemp[menu.day_of_week]) {
              weekDataTemp[menu.day_of_week].meal = menu.meal;
            }
          });
        }

        // Popola i dati delle merende
        if (!snackError && snackData) {
          snackData.forEach((snack: any) => {
            if (weekDataTemp[snack.day_of_week]) {
              weekDataTemp[snack.day_of_week].snack = snack.snack;
            }
          });
        }

        setWeekData(weekDataTemp);
      } catch (error) {
        console.error("Errore nel recupero dei dati:", error);
      } finally {
        setLoading(false);
      }
    },
    [daysOfWeek]
  );

  useEffect(() => {
    const initializeCurrentWeek = async () => {
      try {
        const rotationStartDate = await getRotationStartDate();
        const currentWeekNumber = getCurrentWeekNumber(rotationStartDate);

        setCurrentWeek(currentWeekNumber);
        setSelectedWeek(currentWeekNumber);

        // Carica i dati della settimana corrente
        await fetchWeekData(currentWeekNumber);
      } catch (error) {
        console.error("Errore nell'inizializzazione:", error);
      }
    };

    initializeCurrentWeek();
  }, [fetchWeekData]);

  // Effect che si attiva quando cambia la settimana selezionata
  useEffect(() => {
    if (selectedWeek && selectedWeek !== currentWeek) {
      fetchWeekData(selectedWeek);
    }
  }, [selectedWeek, currentWeek, fetchWeekData]);

  if (loading) {
    return <LoadingSpinner message="Caricamento menù settimanale..." />;
  }

  const handleWeekChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const weekNumber = parseInt(event.target.value);
    setSelectedWeek(weekNumber);
  };

  return (
    <div className="min-h-screen px-4 py-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Menù Settimanale
        </h1>

        {/* Selettore Settimana */}
        <div className="flex justify-center items-center gap-4 mb-6">
          <label
            htmlFor="week-selector"
            className="text-lg font-semibold text-gray-700"
          >
            Settimana:
          </label>
          <select
            id="week-selector"
            value={selectedWeek || currentWeek || 1}
            onChange={handleWeekChange}
            className="px-4 py-2 border-2 border-soft-blue rounded-xl text-gray-700 bg-white focus:ring-2 focus:ring-soft-blue focus:border-soft-blue transition-all duration-200 shadow-lg"
          >
            {[1, 2, 3, 4].map((weekNum) => (
              <option key={weekNum} value={weekNum}>
                Settimana {weekNum}
                {weekNum === currentWeek ? " (Attuale)" : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedWeek && (
          <p className="text-lg text-gray-600">
            {formatWeekSelection(selectedWeek)}
            {selectedWeek === currentWeek && " (Questa settimana)"}
          </p>
        )}
      </div>

      {/* Menu Cards per ogni giorno */}
      <div className="space-y-4 mb-8">
        {daysOfWeek.map((day) => {
          const dayData = weekData[day];
          const dayName = getDayName(day);

          return (
            <div
              key={day}
              className="bg-white rounded-2xl shadow-lg p-6 border-2 border-soft-blue"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-3">
                  {day === 1 && "🍝"}
                  {day === 2 && "🍔"}
                  {day === 3 && "🐟"}
                  {day === 4 && "🥘"}
                  {day === 5 && "🍄"}
                </span>
                {dayName}
              </h3>

              <div className="grid gap-4">
                {/* Pranzo */}
                <div className="bg-soft-pink rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-xl mr-2">🍽️</span>
                    <h4 className="font-semibold text-gray-800">Pranzo</h4>
                  </div>
                  {dayData?.meal ? (
                    <p className="text-gray-700 ml-7 whitespace-pre-line">
                      {dayData.meal}
                    </p>
                  ) : (
                    <p className="text-gray-500 italic ml-7">Non disponibile</p>
                  )}
                </div>

                {/* Merenda */}
                <div className="bg-soft-yellow rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-xl mr-2">🍪</span>
                    <h4 className="font-semibold text-gray-800">Merenda</h4>
                  </div>
                  {dayData?.snack ? (
                    <p className="text-gray-700 ml-7 whitespace-pre-line">
                      {dayData.snack}
                    </p>
                  ) : (
                    <p className="text-gray-500 italic ml-7">Non disponibile</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Torna alla Home */}
      <div className="text-center">
        <Link
          to="/"
          className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg"
        >
          Torna alla Home
        </Link>
      </div>
    </div>
  );
};

export default MenuPage;
